<template>
    <div class="summary-tab p-3">
        <b-row class="mb-4">
            <b-col md="6" class="mb-3">
                <b-card no-body class="h-100 shadow-sm border">
                    <b-card-header class="font-weight-bold bg-light">
                        {{ $t('application_info_title') }}
                    </b-card-header>
                    <b-card-body class="font-size-13 text-muted">
                        <template v-if="formData.info">
                            <div><strong>{{ $t('name_surname') }}:</strong> {{ formData.info.name }} {{ formData.info.surname }}</div>
                            <div><strong>{{ $t('national_id') }}:</strong> {{ userData.national_id || userData.passport_number || '-' }}</div>
                            <div><strong>{{ $t('email') }}:</strong> {{ formData.info.email }}</div>
                            <div><strong>{{ $t('phone') }}:</strong> {{ formData.info.phone }}</div>
                            <div><strong>{{ $t('education_status') }}:</strong> {{ formData.info.education_status_name || formData.info.education_status }}</div>
                            <div><strong>{{ $t('last_school') }}:</strong> {{ formData.info.last_school_name || formData.info.last_school }}</div>
                        </template>
                        <div v-else>{{ $t('no_info_found') }}</div>
                    </b-card-body>
                </b-card>
            </b-col>

            <b-col md="6" class="mb-3">
                <b-card no-body class="h-100 shadow-sm border">
                    <b-card-header class="font-weight-bold bg-light">
                        {{ $t('program_selection_title') }}
                    </b-card-header>
                    <b-card-body class="font-size-13 text-muted">
                        <div><strong>{{ $t('faculty') }}:</strong> {{ formData.program.appliedProgram?.faculty_name || formData.program.appliedProgram?.faculty || '-' }}</div>
                        <div><strong>{{ $t('program') }}:</strong> {{ formData.program.appliedProgram?.program_name || formData.program.appliedProgram?.program || '-' }}</div>
                        <div><strong>{{ $t('program_level') }}:</strong> {{ formData.program.appliedProgram?.level_name || formData.program.appliedProgram?.level || '-' }}</div>
                    </b-card-body>
                </b-card>
            </b-col>

            <b-col md="6" class="mb-3">
                <b-card no-body class="h-100 shadow-sm border">
                    <b-card-header class="font-weight-bold bg-light">
                        {{ $t('course_registration_title') }}
                    </b-card-header>
                    <b-card-body class="font-size-13 text-muted">
                        <ul class="pl-3 mb-0" v-if="courseList.length">
                            <li v-for="(course, idx) in courseList" :key="idx">
                                {{ course.course_code }} - {{ course.course_name }}
                            </li>
                        </ul>
                        <div v-else>{{ $t('no_course_added') }}</div>
                    </b-card-body>
                </b-card>
            </b-col>

            <b-col md="6" class="mb-3">
                <b-card no-body class="h-100 shadow-sm border">
                    <b-card-header class="font-weight-bold bg-light">
                        {{ $t('documents_title') }}
                    </b-card-header>
                    <b-card-body class="font-size-13 text-muted">
                        <ul class="pl-3 mb-0" v-if="documentList.length">
                            <li v-for="(doc, idx) in documentList" :key="idx">
                                {{ doc.label }}
                                <i class="ri-check-line text-success font-weight-bold"></i>
                            </li>
                        </ul>
                        <div v-else>{{ $t('no_document_uploaded') }}</div>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>

        <h5 class="font-weight-bold mb-3">{{ $t('information_text') }}</h5>
        <div class="border p-3 rounded bg-light font-size-12 text-justify mb-4" style="max-height: 250px; overflow-y: auto;">
            <p class="font-weight-bold mb-1" v-html="$t('guest_student_general_rule')"></p>

            <template v-if="!isGraduate">
                <p class="font-weight-bold mt-3 mb-1" v-html="$t('guest_student_ug_title')"></p>
                <p v-html="$t('guest_student_ug_body')"></p>
            </template>
            <template v-else>
                <p class="font-weight-bold mt-3 mb-1" v-html="$t('guest_student_grad_title')"></p>
                <p v-html="$t('guest_student_grad_body')"></p>
            </template>
        </div>

        <b-form-group class="mb-3">
            <b-form-checkbox v-model="agreements.rules">{{ $t('guest_student_rules_agreement') }}</b-form-checkbox>
        </b-form-group>
        <b-form-group class="mb-5">
            <b-form-checkbox v-model="agreements.kvkk">{{ $t('guest_student_kvkk_agreement') }}</b-form-checkbox>
        </b-form-group>

        <div class="d-flex flex-column flex-sm-row justify-content-center align-items-center pb-5">
            <b-button variant="primary" @click="submitApplication" :disabled="!isFormValid || loading" class="w-100 w-sm-auto px-5 font-weight-bold shadow-sm font-size-15">
                <b-spinner small v-if="loading" class="mr-2" />
                {{ $t('guest_student_save_application') }}
            </b-button>
        </div>
    </div>
</template>

<script>
import VisitorStudentApplicationServices from "@/services/VisitorStudentApplicationServices";

export default {
    name: "SummaryTab",
    props: {
        userData: {
            type: Object,
            default: () => ({})
        },
        formData: {
            type: Object,
            default: () => (
                {
                    info: {},
                    program: {},
                    courses: {},
                    documents: {}
                })
        }
    },
    data() {
        return {
            loading: false,
            agreements: {
                rules: false,
                kvkk: false
            }
        }
    },
    computed: {
        isGraduate() {
            return ['YL', 'D'].includes(this.formData.programLevel);
        },
        isFormValid() {
            return this.agreements.rules && this.agreements.kvkk;
        },
        documentList() {
            const formDocs = this.formData.documents?.documents || [];
            if (formDocs.length) {
                return formDocs
                    .filter(d => d.files && d.files.length > 0 || d.alreadyUploaded)
                    .map(d => ({ label: d.name }));
            }
            const serverDocs = this.formData.serverDocuments || [];
            return serverDocs
                .filter(d => d.status === 'uploaded')
                .map(d => ({ label: d.document_type_name || d.document_type }));
        },
        courseList() {
            return this.formData.courses?.courses || [];
        }
    },
    methods: {
        async submitApplication() {
            this.loading = true;
            try {
                const credentials = this.$store.getters['VisitorStudentApplicationStore/getCredentials'];
                const payload = {
                    ...credentials,
                    is_information_notice_approved: this.agreements.rules,
                    is_kvkk_approved: this.agreements.kvkk
                };
                await VisitorStudentApplicationServices.submitApplication(payload);
                this.$emit('submit');
            } catch (error) {
                this.showErrors(error);
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style scoped>
</style>
